import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  useToast,
  Image,
  Center,
  Box,
  Button,
  useDisclosure,
  Text,
  ModalFooter,
  useBoolean,
} from '@chakra-ui/react';
import { Api } from 'api';
import React, { useState } from 'react';
import { useQuery } from 'react-query';
import {
  IntegrationQueryCacheKey,
  ProductType,
  ProductTypeQueryCacheKey,
  TramerDamageData,
  TramerDamageDateKey,
  TramerDamageDateKeyResponse,
  TramerDamageResponseData,
  TramerQueryType,
} from 'types';
import { Trans } from '@lingui/macro';
import { LoadingIndicator } from './LoadingIndicator';

export interface ExpertiseTramerViewProps extends Omit<ModalProps, 'children'> {
  customerId: number;
}

export const ExpertiseTramer: React.FC<ExpertiseTramerViewProps> = ({ customerId, ...modalProps }) => {
  const { isOpen: isConfirmDialogOpen, onOpen: onConfirmDialogOpen, onClose: onConfirmDialogClose } = useDisclosure();
  const [isDamageDataKeyLoading, { on: onDamageDataKeyLoading, off: offDamageDataKeyLoading }] = useBoolean();
  const [changedPart, setChangedPart] = useState<TramerDamageResponseData | null>(null);
  const [currentDamageDateKey, setCurrentDamageDateKey] = useState<string>();
  const {
    isOpen: isDamageDateKeyDialogOpen,
    onOpen: onDamageDateKeyDialogOpen,
    onClose: onDamageDateKeyDialogClose,
  } = useDisclosure();
  const toast = useToast();
  const { data: tramerData, isLoading } = useQuery(
    [IntegrationQueryCacheKey.GetExpertiseTramerDamage, customerId],
    async () => {
      const { data } = await Api.Integration.getTramerDamage(customerId);
      const { tramerResult } = data;
      let convertedTramerResult = (
        tramerResult.damageDateKeys ? JSON.parse(tramerResult.damageDateKeys) : []
      ) as TramerDamageDateKeyResponse[];
      if (typeof convertedTramerResult === 'string') {
        convertedTramerResult = JSON.parse(convertedTramerResult);
      }
      return {
        url: tramerResult.url,
        damageDateKeys: convertedTramerResult
          .filter(
            (damageDateKey: TramerDamageDateKeyResponse) =>
              damageDateKey.value !== '' || damageDateKey.damageDateKey !== '',
          )
          .map((damageDateKey: TramerDamageDateKeyResponse) => ({
            orderId: damageDateKey.orderId,
            value: damageDateKey.value ?? damageDateKey.damageDateKey,
          })),
      } as TramerDamageData;
    },
    {
      retry: 0,
      enabled: modalProps.isOpen,
      refetchOnWindowFocus: false,
      onError: (error: any) => {
        const {
          response: {
            data: { message },
          },
        } = error;
        toast({
          title: message,
          isClosable: true,
          status: 'error',
        });
      },
    },
  );

  const { refetch: refetchDamageDateKeySet, data: damageDateKeySet } = useQuery(
    [IntegrationQueryCacheKey.GetTramerOldQueries, customerId],
    async () => {
      const { data } = await Api.Integration.getOldTramerQueries(customerId, TramerQueryType.ChangedPart);
      const dataSet = new Set<string>([]);
      data.forEach((value: TramerDamageResponseData) => {
        dataSet.add(value.damageDateKeyValue);
      });
      return dataSet;
    },
    {
      refetchOnWindowFocus: false,
      retry: 0,
      enabled: modalProps.isOpen,
    },
  );

  const { data: TramerResultProductPrices } = useQuery(
    ProductTypeQueryCacheKey.GetTramerQuery,
    async () => {
      const response = await Api.Util.getProductPriceByType(ProductType.TramerQuery);
      return response.data;
    },
    {
      // enabled: isDamageDateKeyDialogOpen,
      refetchOnWindowFocus: false,
      cacheTime: 0,
      onError: (error: any) => {
        const {
          response: {
            data: { message },
          },
        } = error;
        toast({
          title: message,
          status: 'error',
          isClosable: true,
        });
      },
    },
  );

  const fetchDamageDateDate = async (damageDateKey: string) => {
    onDamageDataKeyLoading();
    try {
      const response = await Api.Integration.getTramerChangedPart(customerId, damageDateKey);
      const damageDateData = response.data.tramerResult;
      setChangedPart(damageDateData);
    } catch (err) {
      console.error(err);
      toast({
        title: 'Parça Değişen Bilgileri Çekilirken Hata Oluştu',
        isClosable: true,
        status: 'error',
      });
    } finally {
      offDamageDataKeyLoading();
    }
  };

  const onDamageDateKeyClicked = (damageDateKey: string) => {
    if (damageDateKeySet?.has(damageDateKey)) {
      onDamageDateKeyDialogOpen();
      fetchDamageDateDate(damageDateKey);
    } else {
      setCurrentDamageDateKey(damageDateKey);
      onConfirmDialogOpen();
    }
  };

  const onDamageDateKeyConfirmedClicked = async () => {
    if (currentDamageDateKey) {
      await fetchDamageDateDate(currentDamageDateKey);
      onDamageDateKeyDialogOpen();
    }
    onConfirmDialogClose();
  };

  // eslint-disable-next-line no-console
  console.log(tramerData);

  return (
    <>
      <Modal
        isOpen={isDamageDateKeyDialogOpen}
        onClose={() => {
          refetchDamageDateKeySet();
          onDamageDateKeyDialogClose();
        }}
        isCentered
        size="full"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Trans>Araç Değişen Sorgu</Trans>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Center>
              {isDamageDataKeyLoading || !changedPart ? (
                <LoadingIndicator />
              ) : (
                <Box maxHeight="80vh" maxWidth="100%" overflowY="auto" marginTop="2rem" padding="1rem">
                  <Image src={changedPart.url} boxSizing="border-box" width="100%" height="auto" />
                </Box>
              )}
            </Center>
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={() => {
                refetchDamageDateKeySet();
                onDamageDateKeyDialogClose();
              }}
            >
              <Trans>Kapat</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isConfirmDialogOpen}
        onClose={onConfirmDialogClose}
        isCentered
        closeOnOverlayClick={!isDamageDataKeyLoading}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Trans>Araç Değişen Sorgu Onay?</Trans>
          </ModalHeader>
          <ModalCloseButton disabled={isDamageDataKeyLoading} />
          <ModalBody>
            {TramerResultProductPrices && (
              <Text>
                <Trans>
                  Hasar Kaydı Sorgusu için ₺
                  {TramerResultProductPrices.buyPrice > 0
                    ? TramerResultProductPrices.buyPrice
                    : TramerResultProductPrices.price}{' '}
                  sorgu bedeli kullanılacaktır. Devam etmek istiyor musunuz ?
                </Trans>
              </Text>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={onDamageDateKeyConfirmedClicked}
              isLoading={isDamageDataKeyLoading}
            >
              <Trans>Evet</Trans>
            </Button>
            <Button onClick={onConfirmDialogClose} disabled={isDamageDataKeyLoading}>
              <Trans>Hayır</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal {...modalProps} isCentered size="full">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Trans>Hasar Kaydı Sorgula</Trans>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isLoading || !tramerData ? (
              <LoadingIndicator />
            ) : (
              <Center>
                <div
                  style={{
                    backgroundImage: `url(${tramerData?.url})`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    position: 'relative',
                    width: '700px',
                    height: '700px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {tramerData &&
                    tramerData.damageDateKeys &&
                    tramerData.damageDateKeys.map((damageDateKey: TramerDamageDateKey) => {
                      return (
                        <div
                          key={damageDateKey.orderId}
                          style={{
                            flexDirection: 'column',
                            display: 'flex',
                            position: 'absolute',
                            width: '100%',
                            left: '20%',
                            top: '10%',
                            marginTop: `${damageDateKey.orderId * 33}px`,
                          }}
                        >
                          {damageDateKey.value && (
                            <Button
                              variant="unstyled"
                              style={{
                                position: 'relative',
                                width: '200px',
                                marginLeft: 'auto',
                                marginRight: 'auto',
                                color: 'blue',
                              }}
                              onClick={() => {
                                onDamageDateKeyClicked(damageDateKey.value);
                              }}
                              colorScheme="blue"
                            >
                              Değişen Sorgu
                            </Button>
                          )}
                        </div>
                      );
                    })}
                </div>
              </Center>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={modalProps.onClose}>
              <Trans>Kapat</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
